<script setup>
import axios from 'axios';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import { formatCurrencySums, formatDateTime } from '../format';
import { t } from '../i18n';
import {
  CATEGORIES, dayLabel, filterSearch, groupByDay, localDateKey, periodLabel, periodParams, periodSuffix, plural,
  readFilter, shortDate,
} from '../operations';
import BottomSheet from './BottomSheet.vue';
import OperationRow from './OperationRow.vue';

// Operations of the selected portfolio (docs/operations/SPEC.md, DESIGN.md §2): category chips with
// several at once (decision 3), a period (decision 7), the total of the filter (decision 6), pages of 50.
const props = defineProps({
  // currentView of the home screen
  portfolio: {
    type: Object,
    required: true,
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  keyConfigured: {
    type: Boolean,
    default: false,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  // Bumped after accounts are imported again: the list is read anew.
  dataVersion: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['back', 'refresh', 'go-settings']);

const filter = ref(readFilter(window.location.search));
const items = ref([]);
const nextCursor = ref(null);
const counts = ref({});
const total = ref(null);
const years = ref([]);
const synced = ref(null);
const loading = ref(true);
// A new filter or portfolio while rows are shown: the old rows are dimmed until the answer.
const reloading = ref(false);
const error = ref('');
const loadingMore = ref(false);
const moreError = ref('');
const expandedId = ref(null);
let request = 0;

const portfolioIds = computed(() => props.portfolio.members.map((m) => m.portfolio_id));
const showAccount = computed(() => portfolioIds.value.length > 1);

function params(cursor = null) {
  return {
    portfolio_ids: portfolioIds.value,
    categories: filter.value.categories,
    ...periodParams(filter.value.period),
    ...(cursor ? { cursor } : {}),
  };
}

async function loadFirst() {
  const id = ++request;
  expandedId.value = null;
  moreError.value = '';
  if (portfolioIds.value.length === 0) {
    items.value = [];
    nextCursor.value = null;
    loading.value = false;
    return;
  }
  loading.value = items.value.length === 0;
  reloading.value = !loading.value;
  error.value = '';
  try {
    const { data } = await axios.get('/operations/list', { params: params() });
    if (id !== request) {
      return;
    }
    items.value = data.items;
    nextCursor.value = data.next_cursor;
    counts.value = data.counts ?? {};
    total.value = data.total;
    years.value = data.years ?? [];
    synced.value = data.synced;
  } catch (e) {
    if (id === request && e?.response?.status !== 401) {
      error.value = extractApiError(e, t('operations.loadFailed'));
    }
  } finally {
    if (id === request) {
      loading.value = false;
      reloading.value = false;
    }
  }
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) {
    return;
  }
  const id = request;
  loadingMore.value = true;
  moreError.value = '';
  try {
    const { data } = await axios.get('/operations/list', { params: params(nextCursor.value) });
    if (id !== request) {
      return;
    }
    items.value = [...items.value, ...data.items];
    nextCursor.value = data.next_cursor;
  } catch (e) {
    if (id === request && e?.response?.status !== 401) {
      moreError.value = t('operations.loadMoreFailed');
    }
  } finally {
    loadingMore.value = false;
  }
}

// The filter lives in the address (DESIGN.md): reload and "back" keep it, a new session does not.
watch(filter, (value) => {
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${filterSearch(value)}`);
  loadFirst();
}, { deep: true });
watch(() => portfolioIds.value.join(','), loadFirst);
watch(() => props.dataVersion, loadFirst);

// Chips: categories with operations in the period, plus the selected ones so they can be cleared.
const visibleCategories = computed(() => CATEGORIES.filter((c) => counts.value[c] || filter.value.categories.includes(c)));
const hasFilter = computed(() => filter.value.categories.length > 0);

function toggleCategory(category) {
  const selected = filter.value.categories;
  filter.value = {
    ...filter.value,
    categories: selected.includes(category) ? selected.filter((c) => c !== category) : [...selected, category],
  };
}

function resetCategories() {
  filter.value = { ...filter.value, categories: [] };
}

const statusText = computed(() => {
  const names = CATEGORIES.filter((c) => filter.value.categories.includes(c)).map((c) => t(`operations.cat.${c}`)).join(', ');
  return `${names} · ${plural(total.value?.count ?? 0, 'operations.count')}`;
});

// Total by currency (decision 6) for the period.
const totalText = computed(() => {
  if (!total.value) {
    return '';
  }
  const sums = new Map(total.value.sums.map((s) => [s.currency, s.amount]));
  return `${formatCurrencySums(sums)} ${periodSuffix(filter.value.period)}`;
});

// Period sheet (decision 7)
const periodSheetOpen = ref(false);
const customFrom = ref('');
const customTo = ref('');

function openPeriodSheet() {
  const period = filter.value.period;
  customFrom.value = period.kind === 'custom' ? period.from : '';
  customTo.value = period.kind === 'custom' ? period.to : '';
  periodSheetOpen.value = true;
}

function choosePeriod(period) {
  periodSheetOpen.value = false;
  filter.value = { ...filter.value, period };
}

const customValid = computed(() => (customFrom.value || customTo.value) && (!customFrom.value || !customTo.value || customFrom.value <= customTo.value));

function applyCustom() {
  if (customValid.value) {
    choosePeriod({ kind: 'custom', from: customFrom.value, to: customTo.value });
  }
}

function isPeriod(kind, year = null) {
  const period = filter.value.period;
  return period.kind === kind && (year === null || period.year === year);
}

const days = computed(() => groupByDay(items.value));

const allMissing = computed(() => synced.value !== null && synced.value.missing >= portfolioIds.value.length);
const freshness = computed(() => (synced.value?.at ? t('operations.syncedAt', { date: formatDateTime(synced.value.at) }) : ''));
const missingText = computed(() => (synced.value?.missing > 0 && !allMissing.value ? t('operations.notSynced', { count: synced.value.missing }) : ''));

const endText = computed(() => {
  const last = items.value[items.value.length - 1];
  return last ? t('operations.end', { date: shortDate(localDateKey(last.executed_at)) }) : '';
});

// Next page before the end is reached (rootMargin 600px); the button stays for the keyboard and reduced motion.
const sentinel = ref(null);
let observer = null;

function observe() {
  observer?.disconnect();
  if (sentinel.value && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        loadMore();
      }
    }, { rootMargin: '600px' });
    observer.observe(sentinel.value);
  }
}

watch(sentinel, () => nextTick(observe));

onMounted(loadFirst);
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <div class="space-y-3 sm:space-y-4">
    <div>
      <button
        v-if="desktop"
        type="button"
        class="mb-2 text-sm text-slate-400 hover:text-slate-200"
        @click="emit('back')"
      >
        ‹ {{ t('operations.backHome') }}
      </button>
      <h1 class="px-1 pt-1 text-[1.75rem] font-bold leading-8 text-slate-100 sm:px-0 sm:pt-0 sm:text-2xl sm:font-semibold">
        {{ t('operations.title') }}
      </h1>
      <p
        v-if="freshness || missingText"
        class="mt-1 px-1 text-[0.8125rem] text-slate-500 sm:px-0"
      >
        {{ freshness }}<template v-if="freshness && missingText">
          ·
        </template><span
          v-if="missingText"
          class="text-amber-300"
        >{{ missingText }}</span>
      </p>
    </div>

    <!-- Filter strip: sticky on a phone (DESIGN.md, «Липкая полоса») -->
    <div
      v-if="!loading && !error && !allMissing && (items.length > 0 || hasFilter || filter.period.kind !== 'all')"
      class="sticky top-[calc(2.75rem+env(safe-area-inset-top))] z-30 -mx-4 bg-slate-950/85 px-4 py-2 backdrop-blur-md sm:static sm:mx-0 sm:max-w-5xl sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
    >
      <div
        class="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
        role="group"
        :aria-label="t('operations.filter')"
      >
        <button
          type="button"
          class="relative flex h-9 shrink-0 items-center gap-1 rounded-full px-3.5 text-[0.875rem] font-medium before:absolute before:inset-x-0 before:-inset-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="filter.period.kind !== 'all' ? 'bg-slate-100 text-slate-950' : 'bg-slate-800 text-slate-300'"
          aria-haspopup="dialog"
          @click="openPeriodSheet"
        >
          {{ filter.period.kind === 'all' ? t('operations.period') : periodLabel(filter.period) }}
          <svg
            class="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="m6 9 6 6 6-6" /></svg>
        </button>
        <span
          class="my-1.5 w-px shrink-0 bg-slate-800"
          aria-hidden="true"
        />
        <button
          type="button"
          class="relative h-9 shrink-0 rounded-full px-3.5 text-[0.875rem] font-medium before:absolute before:inset-x-0 before:-inset-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="!hasFilter ? 'bg-slate-100 text-slate-950' : 'bg-slate-800 text-slate-300'"
          :aria-pressed="!hasFilter"
          @click="resetCategories"
        >
          {{ t('operations.all') }}
        </button>
        <button
          v-for="category in visibleCategories"
          :key="category"
          type="button"
          class="relative flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[0.875rem] font-medium before:absolute before:inset-x-0 before:-inset-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="filter.categories.includes(category) ? 'bg-slate-100 text-slate-950' : 'bg-slate-800 text-slate-300'"
          :aria-pressed="filter.categories.includes(category)"
          @click="toggleCategory(category)"
        >
          <svg
            v-if="filter.categories.includes(category)"
            class="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
          {{ t(`operations.cat.${category}`) }}
          <span
            v-if="desktop && counts[category]"
            class="tabular-nums"
            :class="filter.categories.includes(category) ? 'text-slate-600' : 'text-slate-500'"
          >{{ counts[category] }}</span>
        </button>
      </div>
      <div
        v-if="hasFilter"
        class="mt-1 flex min-h-8 items-center justify-between gap-3 text-[0.8125rem] sm:text-sm"
      >
        <span class="min-w-0 text-slate-300">
          <span class="block truncate">{{ statusText }}</span>
          <span
            v-if="totalText"
            class="block truncate tabular-nums text-slate-100"
          >{{ totalText }}</span>
        </span>
        <button
          type="button"
          class="relative shrink-0 font-medium text-slate-100 before:absolute before:-inset-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          @click="resetCategories"
        >
          {{ t('operations.reset') }}
        </button>
      </div>
    </div>

    <!-- First load -->
    <div
      v-if="loading"
      class="space-y-3"
      aria-busy="true"
    >
      <span class="sr-only">{{ t('common.loading') }}</span>
      <div class="flex gap-2">
        <div
          v-for="n in 3"
          :key="n"
          class="h-9 w-20 rounded-full bg-slate-800 motion-safe:animate-pulse"
        />
      </div>
      <div class="space-y-2 rounded-3xl bg-slate-900 p-5 sm:rounded-lg">
        <div
          v-for="n in 6"
          :key="n"
          class="h-14 rounded-xl bg-slate-800 motion-safe:animate-pulse"
        />
      </div>
    </div>

    <div
      v-else-if="error"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:rounded-lg"
    >
      <p class="text-[0.9375rem] text-rose-300">
        {{ error }}
      </p>
      <button
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        @click="loadFirst"
      >
        {{ t('common.retry') }}
      </button>
    </div>

    <div
      v-else-if="portfolioIds.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center text-[0.9375rem] text-slate-400 sm:rounded-lg"
    >
      {{ t('operations.noAccounts') }}
    </div>

    <!-- Never rebuilt: nothing stored yet -->
    <div
      v-else-if="allMissing && items.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:rounded-lg"
    >
      <p class="text-[1.0625rem] font-semibold text-slate-100">
        {{ t('operations.emptyNotLoaded') }}
      </p>
      <p class="mt-2 text-[0.9375rem] text-slate-400">
        {{ t('operations.emptyNotLoadedText') }}
      </p>
      <button
        v-if="keyConfigured"
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 disabled:opacity-50 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        :disabled="busy"
        @click="emit('refresh')"
      >
        {{ busy ? t('refresh.running') : t('operations.loadNow') }}
      </button>
      <template v-else>
        <p class="mt-3 text-[0.8125rem] text-amber-300">
          {{ t('refresh.noKey') }}
        </p>
        <button
          type="button"
          class="mt-3 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
          @click="emit('go-settings')"
        >
          {{ t('live.openProfile') }}
        </button>
      </template>
    </div>

    <div
      v-else-if="items.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:rounded-lg"
    >
      <p class="text-[0.9375rem] text-slate-300">
        {{ hasFilter || filter.period.kind !== 'all' ? t('operations.emptyFilter') : t('operations.empty') }}
      </p>
      <button
        v-if="hasFilter || filter.period.kind !== 'all'"
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        @click="filter = { categories: [], period: { kind: 'all' } }"
      >
        {{ t('operations.resetFilter') }}
      </button>
    </div>

    <template v-else>
      <section
        class="rounded-3xl bg-slate-900 py-2 transition-opacity sm:max-w-5xl sm:rounded-lg sm:border sm:border-slate-700 sm:bg-slate-900/40 sm:py-0"
        :class="reloading ? 'opacity-50' : ''"
        :aria-busy="loadingMore || reloading"
      >
        <div
          v-for="day in days"
          :key="day.key"
        >
          <h2
            class="px-5 pb-1 pt-3 text-[0.8125rem] text-slate-500 first-letter:uppercase sm:bg-slate-900/60 sm:py-1.5 sm:text-xs"
          >
            {{ dayLabel(day.key, { full: desktop }) }}
          </h2>
          <ul>
            <li
              v-for="op in day.items"
              :key="op.id"
            >
              <OperationRow
                :op="op"
                :desktop="desktop"
                :show-account="showAccount"
                :expanded="expandedId === op.id"
                @toggle="expandedId = expandedId === op.id ? null : op.id"
              />
            </li>
          </ul>
        </div>
        <div
          v-if="loadingMore"
          class="flex h-12 items-center justify-center"
        >
          <span class="size-5 rounded-full border-2 border-slate-600 border-t-slate-200 motion-safe:animate-spin" />
          <span class="sr-only">{{ t('common.loading') }}</span>
        </div>
        <p
          v-else-if="moreError"
          class="flex items-center justify-center gap-2 py-3 text-[0.8125rem] text-rose-300"
        >
          {{ moreError }} ·
          <button
            type="button"
            class="font-medium text-slate-100"
            @click="loadMore"
          >
            {{ t('common.retry') }}
          </button>
        </p>
      </section>

      <template v-if="nextCursor">
        <div
          ref="sentinel"
          aria-hidden="true"
        />
        <button
          v-if="!loadingMore && !moreError"
          type="button"
          class="h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
          :class="desktop ? '' : 'sr-only focus-visible:not-sr-only motion-reduce:not-sr-only'"
          @click="loadMore"
        >
          {{ t('operations.loadMore') }}
        </button>
      </template>
      <p
        v-else
        class="py-4 text-center text-[0.8125rem] text-slate-500"
      >
        {{ endText }}
      </p>
    </template>

    <BottomSheet
      :open="periodSheetOpen"
      :title="t('operations.period')"
      @close="periodSheetOpen = false"
    >
      <div class="pb-2">
        <button
          v-for="option in [{ kind: 'all' }, ...years.map((year) => ({ kind: 'year', year }))]"
          :key="option.kind === 'year' ? option.year : option.kind"
          type="button"
          class="flex min-h-12 w-full items-center justify-between px-5 text-left text-[0.9375rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          :class="isPeriod(option.kind, option.year ?? null) ? 'font-semibold text-slate-100' : 'text-slate-200'"
          :aria-current="isPeriod(option.kind, option.year ?? null) ? 'true' : undefined"
          @click="choosePeriod(option)"
        >
          {{ periodLabel(option) }}
          <svg
            v-if="isPeriod(option.kind, option.year ?? null)"
            class="size-4 text-emerald-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
        </button>
        <div class="mt-2 border-t border-slate-800 px-5 pt-4">
          <p class="text-[0.8125rem] uppercase tracking-wide text-slate-500">
            {{ t('operations.customPeriod') }}
          </p>
          <div class="mt-2 grid grid-cols-2 gap-3">
            <label class="block text-[0.8125rem] text-slate-400">
              {{ t('operations.from') }}
              <input
                v-model="customFrom"
                type="date"
                class="mt-1 h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-[0.9375rem] text-slate-100 [color-scheme:dark]"
              >
            </label>
            <label class="block text-[0.8125rem] text-slate-400">
              {{ t('operations.to') }}
              <input
                v-model="customTo"
                type="date"
                class="mt-1 h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-[0.9375rem] text-slate-100 [color-scheme:dark]"
              >
            </label>
          </div>
          <button
            type="button"
            class="mt-3 h-11 w-full rounded-2xl bg-slate-100 text-[0.9375rem] font-medium text-slate-950 disabled:opacity-40"
            :disabled="!customValid"
            @click="applyCustom"
          >
            {{ t('operations.apply') }}
          </button>
        </div>
      </div>
    </BottomSheet>
  </div>
</template>
